import React, { useEffect, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Briefcase, GitMerge, Building2, Globe2, Layers, Factory, Users, Cpu } from 'lucide-react';
const challenges = [{
  icon: GitMerge,
  title: 'The Grass Valley–Avid Joint Venture Integration',
  description: 'Reforging a management team who had spent the previous decade as vicious competitors.'
}, {
  icon: Building2,
  title: 'The Vitec / Videndum Conglomeration',
  description: 'Creating a common vision and synergies for dozens of independently managed divisions and geographic hubs.'
}, {
  icon: Cpu,
  title: 'Council of Leaders for AI in Mortgage',
  description: 'Embracing fast-moving technological change in a time of market constraints.'
}, {
  icon: Factory,
  title: 'The Vinten–Sachtler Transition',
  description: 'Optimizing manufacturing offshoring while balancing first-world organized labor interests and developing-world upskilling.'
}, {
  icon: Users,
  title: 'The International City Mortgage Cultural Challenge',
  description: 'Building loyalty and cohesion in an ecosystem of independent operators and ruthless competition.'
}, {
  icon: Briefcase,
  title: 'The ParkerVision Sale',
  description: 'Re-configuring a tech start-up for a commercial sale and integrating management to deliver synergy and strategic value.'
}, {
  icon: Layers,
  title: 'The Pinnacle Integration',
  description: 'Laying out new workflows and product marketing strategies in a complex technology merger.'
}, {
  icon: Globe2,
  title: 'The Clear-Com–Drake Merger',
  description: 'Bridging cultures across the pond — and globally.'
}];
export function MattDanilowiczBio() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="pt-28 pb-24 md:pt-32 md:pb-32" data-id="element-175">
      <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-176">
        {/* Back link */}
        <motion.div initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10" data-id="element-177">
          <Link to="/#our-people" className="inline-flex items-center gap-2 text-sm text-offwhite-muted hover:text-amber transition-colors" data-id="element-178">
            <ArrowLeft size={16} data-id="element-179" />
            Back to Our People
          </Link>
        </motion.div>

        {/* Hero: headshot + name headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center" data-id="element-180">
          <div className="md:col-span-5" data-id="element-181">
            <div className="overflow-hidden rounded-md border border-white/10 bg-navy-light" data-id="element-182">
              <img src="https://cdn.magicpatterns.com/uploads/crA2rE5Xa7vZNEg7JDpcd1/headshot_high_quality.jpg" alt="Headshot of Matt Danilowicz" className="w-full h-auto object-cover" data-id="element-183" />
            </div>
          </div>

          <div className="md:col-span-7" data-id="element-184">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-amber mb-4" data-id="element-185">
              Partner Bio
            </p>
            <h1 className="text-5xl md:text-7xl font-serif text-offwhite leading-[1.05] mb-4" data-id="element-186">
              Matt Danilowicz
            </h1>
            <p className="text-lg md:text-xl text-offwhite-muted font-light" data-id="element-187">
              Chief Executive
            </p>
          </div>
        </motion.div>

        {/* Bio body */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-16 md:mb-20 space-y-6" data-id="element-188">
          <p className="text-lg md:text-xl text-offwhite font-light leading-relaxed" data-id="element-189">
            With more than twenty years' experience as a CEO for public and
            private companies, Matt understands the challenge of building
            effective leadership teams while balancing the demands of owners,
            shareholders, employees, customers and regulators.
          </p>
          <p className="text-lg text-offwhite-muted font-light leading-relaxed" data-id="element-190">
            He is an avid practitioner of best-selling author Pat Lencioni's{' '}
            <span className="text-offwhite italic" data-id="element-191">Advantage</span> methodology,
            consulting to deliver{' '}
            <span className="text-amber" data-id="element-192">organizational clarity</span>,
            collaborative workflows, and shared vision.
          </p>
        </motion.div>

        {/* Sub-headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6
      }} className="mb-10 md:mb-12" data-id="element-193">
          <p className="text-sm font-medium tracking-[0.2em] uppercase text-amber mb-4" data-id="element-194">
            Experience
          </p>
          <h2 className="text-3xl md:text-4xl font-serif text-offwhite leading-tight" data-id="element-195">
            Real-world leadership challenges.
          </h2>
        </motion.div>

        {/* Challenges grid */}
        <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-20" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.07
          }
        }
      }} data-id="element-196">
          {challenges.map((c, i) => {
          const Icon = c.icon;
          return <motion.li key={c.title} variants={{
            hidden: {
              opacity: 0,
              y: 14
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.5,
                ease: 'easeOut'
              }
            }
          }} className="relative flex flex-col h-full p-6 md:p-7 bg-navy-light/40 border border-white/10 rounded-md hover:border-amber/40 transition-colors" data-id="element-197">
                <div className="flex items-center gap-3 mb-3" data-id="element-198">
                  <div className="w-9 h-9 rounded-full bg-navy border border-amber/40 flex items-center justify-center flex-shrink-0" data-id="element-199">
                    <Icon size={16} className="text-amber" strokeWidth={1.75} data-id="element-200" />
                  </div>
                  <span className="text-xs font-medium tracking-[0.2em] uppercase text-amber" data-id="element-201">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-xl md:text-[1.35rem] font-serif text-offwhite leading-snug mb-2" data-id="element-202">
                  {c.title}
                </h3>
                <p className="text-sm md:text-base text-offwhite-muted font-light leading-relaxed" data-id="element-203">
                  {c.description}
                </p>
              </motion.li>;
        })}
        </motion.ul>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-navy-light/50 border border-white/10 rounded-md p-8" data-id="element-204">
          <div data-id="element-205">
            <h3 className="text-2xl font-serif text-offwhite mb-1" data-id="element-206">
              Let's talk through your transition.
            </h3>
            <p className="text-sm text-offwhite-muted font-light" data-id="element-207">
              Reach out directly to start a conversation.
            </p>
          </div>
          <a href="mailto:matt@mattdadvisory.com" className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-amber text-navy font-medium rounded-sm hover:bg-amber-hover transition-all flex-shrink-0" data-id="element-208">
            Contact Matt
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-209" />
          </a>
        </motion.div>
      </div>
    </main>;
}